import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {z} from 'zod';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {ProductCardScratchpadState} from '../../scratchpad/productCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import {RelationType} from 'common/src/modules/relatedItems/relatedItems.types';
import {clinchEnums} from '@cl/shared';
import {CLEnumKeys, CLEnumType} from 'common/src/modules/enums/enums.types';
import getProductStrategyRelatedLogics from 'common/src/modules/campaigns/utils/getProductStrategyRelatedLogics';
import {relationTypeToUiName} from 'common/src/modules/relatedItems/relatedItems.consts';
import {IdPrefix} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/idHelpers.enums';
import parseId from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/utils/parseId';
import describeIdFormat from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/utils/describeIdFormat';
import toId from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/utils/toId';
import formatParseError from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/utils/formatParseError';

type SetRelatedItemRelationType =
  | RelationType.ProductSet
  | RelationType.TopItems
  | RelationType.CategoryTopItems;

const relationTypeToPrefix: Record<SetRelatedItemRelationType, IdPrefix> = {
  [RelationType.ProductSet]: IdPrefix.ProductSet,
  [RelationType.TopItems]: IdPrefix.TopItems,
  [RelationType.CategoryTopItems]: IdPrefix.CategoryTopItems,
};

function buildRelatedItemSchema(relationType: SetRelatedItemRelationType) {
  const prefixType = relationTypeToPrefix[relationType];

  return z.object({
    relatedItemLogicId: z.string().describe(describeIdFormat(prefixType)),
  });
}

export default function setRelatedItemTool(relationType: SetRelatedItemRelationType) {
  const schema = buildRelatedItemSchema(relationType);
  const prefixType = relationTypeToPrefix[relationType];

  async function execute({
    input: {relatedItemLogicId},
    toolUtils: {agentScratchpad},
  }: ToolExecuteParams<typeof schema, ProductCardScratchpadState>): Promise<ToolResult> {
    const validatedScratchpad = validateAgentScratchpad<ProductCardScratchpadState>(
      agentScratchpad,
      NodeType.PRODUCTS,
    );

    const parseResult = parseId(relatedItemLogicId, prefixType);

    if (parseResult.ok === false) {
      return toolError(formatParseError(parseResult.error));
    }

    const numericId = parseResult.numericId;
    const feedId = validatedScratchpad.card.feedId;

    if (!feedId) {
      return toolError(`Feed is not set for this card`);
    }

    const strategy = validatedScratchpad.card.productsSelectionStrategy;

    if (!strategy) {
      return toolError(`Strategy is not set for this card. Please set a strategy first.`);
    }

    const campaignEnumMapper = clinchEnums.loadedEnumsSubject.data[CLEnumType.CampaignPage];

    if (!campaignEnumMapper) {
      return toolError('Strategies enum is not loaded');
    }

    const {isTopItemsOriented, isCategoryTopItemsOriented, isRetargetingOriented} =
      getProductStrategyRelatedLogics(campaignEnumMapper, strategy);

    let isSupported = false;
    const relationTypeName = relationTypeToUiName[relationType];

    switch (relationType) {
      case RelationType.ProductSet:
        isSupported = isRetargetingOriented;
        break;
      case RelationType.CategoryTopItems:
        isSupported = isCategoryTopItemsOriented;
        break;
      case RelationType.TopItems:
        isSupported = isTopItemsOriented;
        break;
    }

    if (!isSupported) {
      const strategyName =
        campaignEnumMapper[CLEnumKeys.CampaignPage.CampaignContentType]?.byValue[strategy]
          ?.uiName || `strategy ${strategy}`;

      return toolError(
        `The selected strategy "${strategyName}" does not support ${relationTypeName}. Please choose a different strategy or use a different configuration.`,
      );
    }

    const availableRelatedItemLogics = validatedScratchpad.card.relatedItemsLogics?.filter(
      (r) => r.relationType === relationType,
    );

    if (!availableRelatedItemLogics?.some((p) => p.id === numericId)) {
      return toolError(
        `${relationTypeName} with ID ${toId(prefixType, numericId)} is not available for this card. Available ${relationTypeName}: ${availableRelatedItemLogics?.map((p) => `${toId(prefixType, p.id)}: ${p.name}`).join(', ')}`,
      );
    }

    switch (relationType) {
      case RelationType.ProductSet:
        validatedScratchpad.card.retargetingProductSetId = numericId;
        break;
      case RelationType.CategoryTopItems:
        validatedScratchpad.card.categoryTopItemsLogicId = numericId;
        break;
      case RelationType.TopItems:
        validatedScratchpad.card.topItemLogicId = numericId;
        break;
    }

    return toolResult(
      `${relationTypeName} set to: ${toId(prefixType, numericId)} (${availableRelatedItemLogics?.find((p) => p.id === numericId)?.name})`,
    );
  }

  return tool({
    name: `${V2CardAgentToolName.SetRelatedItemLogic}${relationTypeToUiName[relationType]?.trim().toLowerCase().replace(/ /g, '')}`,
    displayName: `Set ${relationTypeToUiName[relationType]}`,
    description: `Set the ${relationTypeToUiName[relationType]} for the card`,
    execute,
    inputSchema: schema,
  });
}
